import { FilterIcon, PlusIcon, XIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Button } from "./button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "./dialog";
import { Input } from "./input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./select";
import type { FwQueryFilter, FwQueryState } from "./useFwQuery";
import type { FilterDef, FilterOperator, FilterType } from "./types";

const OPERATOR_OPTIONS: Record<
	FilterType,
	{ label: string; value: FilterOperator }[]
> = {
	text: [
		{ label: "Equals", value: "=" },
		{ label: "Contains", value: "contains" },
		{ label: "Starts with", value: "starts with" },
		{ label: "Ends with", value: "ends with" },
		{ label: "Not equal", value: "<>" },
	],
	number: [
		{ label: "Equals", value: "=" },
		{ label: "Greater than", value: ">" },
		{ label: "Greater or equal", value: ">=" },
		{ label: "Less than", value: "<" },
		{ label: "Less or equal", value: "<=" },
		{ label: "Not equal", value: "<>" },
	],
	date: [
		{ label: "Equals", value: "=" },
		{ label: "After", value: ">" },
		{ label: "On or after", value: ">=" },
		{ label: "Before", value: "<" },
		{ label: "On or before", value: "<=" },
		{ label: "Not equal", value: "<>" },
	],
	select: [
		{ label: "Equals", value: "=" },
		{ label: "Not equal", value: "<>" },
		{ label: "In", value: "in" },
		{ label: "Not in", value: "not in" },
	],
	boolean: [
		{ label: "Equals", value: "=" },
		{ label: "Not equal", value: "<>" },
	],
};

const OPERATOR_LABELS: Record<FilterOperator, string> = {
	"=": "Equals",
	"<>": "Not equal",
	contains: "Contains",
	"does not contain": "Does not contain",
	in: "In",
	"not in": "Not in",
	"starts with": "Starts with",
	"ends with": "Ends with",
	">": "After",
	">=": "On or after",
	"<": "Before",
	"<=": "On or before",
};

type FilterOp = FilterOperator;
type FilterValueType = string;

const DEFAULT_OPERATOR_OPTION = {
	label: "Equals",
	value: "=" as FilterOperator,
};

const buildOperatorOption = (
	value: FilterOperator,
): { label: string; value: FilterOperator } => ({
	label: OPERATOR_LABELS[value] ?? value,
	value,
});

function resolveOperatorOptions<T>(filter: FilterDef<T>): {
	options: { label: string; value: FilterOperator }[];
	isFixed: boolean;
} {
	const baseOptions = OPERATOR_OPTIONS[filter.type] ?? [
		DEFAULT_OPERATOR_OPTION,
	];
	const mode = filter.operatorMode;

	if (!mode || mode === "free") {
		return { options: baseOptions, isFixed: false };
	}

	if ("fixed" in mode) {
		const fixedOption =
			baseOptions.find((option) => option.value === mode.fixed) ??
			buildOperatorOption(mode.fixed);
		return { options: [fixedOption], isFixed: true };
	}

	if ("allow" in mode) {
		const allowedSet = new Set(mode.allow);
		const filtered = baseOptions.filter((option) =>
			allowedSet.has(option.value),
		);
		const supplemental = mode.allow
			.filter((op) => !filtered.some((option) => option.value === op))
			.map(buildOperatorOption);
		const combined =
			filtered.length > 0 || supplemental.length > 0
				? [...filtered, ...supplemental]
				: baseOptions;
		return { options: combined, isFixed: mode.allow.length === 1 };
	}

	return { options: baseOptions, isFixed: false };
}

function enforceOperator<T>(
	filter: FilterDef<T>,
	candidate: FilterOp,
): FilterOp {
	const mode = filter.operatorMode;
	if (!mode || mode === "free") {
		return candidate;
	}
	if ("fixed" in mode) {
		return mode.fixed as FilterOp;
	}
	if ("allow" in mode) {
		return (
			mode.allow.includes(candidate) ? candidate : (mode.allow[0] ?? candidate)
		) as FilterOp;
	}
	return candidate;
}

function getDefaultOperator<T>(filter: FilterDef<T>): FilterOp {
	const { options } = resolveOperatorOptions(filter);
	if (options.length === 0) {
		return enforceOperator(filter, DEFAULT_OPERATOR_OPTION.value);
	}
	return enforceOperator(filter, options[0].value as FilterOp);
}

interface FilterValue {
	id: string;
	value: FilterValueType;
	op: FilterOp;
}

interface DataTableAdvancedFiltersProps<T> {
	filters: FilterDef<T>[];
	query: FwQueryState<T>;
}

function createDefaultFilterRow<T>(filters: FilterDef<T>[]): FilterValue {
	const firstFilter = filters[0];
	if (!firstFilter) {
		return { id: "", value: "", op: "=" };
	}

	return {
		id: firstFilter.id,
		value: "",
		op: getDefaultOperator(firstFilter),
	};
}

function DataTableAdvancedFilters<T>({
	filters,
	query,
}: DataTableAdvancedFiltersProps<T>) {
	const [open, setOpen] = useState(false);
	const [appliedFilters, setAppliedFilters] = useState<FilterValue[]>([]);
	const [errors, setErrors] = useState<Record<number, string>>({});

	// Effect to synchronize dialog's internal appliedFilters with query.filters from useFwQuery when dialog opens
	useEffect(() => {
		if (open) {
			const newDialogAppliedFilters = query.filters
				.map((qf: FwQueryFilter<T>) => {
					if (qf.field === "search") return undefined;

					const matchingFilters = filters.filter((fd) => {
						const targetField = (fd.field ?? fd.id)?.toString();
						return targetField === qf.field.toString();
					});

					if (matchingFilters.length === 0) {
						return undefined;
					}

					const filterDef =
						matchingFilters.find(
							(fd) =>
								enforceOperator(fd, qf.op as FilterOp) === (qf.op as FilterOp),
						) ?? matchingFilters[0];

					if (!filterDef) return undefined;

					let dialogValue: FilterValueType = qf.value;
					switch (filterDef.type) {
						case "number": {
							const num = parseFloat(qf.value);
							dialogValue = Number.isNaN(num) ? "" : num.toString();
							break;
						}
						case "boolean":
							dialogValue =
								qf.value.toLowerCase() === "true" ? "true" : "false";
							break;
						default:
							dialogValue = qf.value;
							break;
					}

					return {
						id: filterDef.id,
						value: dialogValue,
						op: enforceOperator(filterDef, qf.op as FilterOp),
					};
				})
				.filter((f): f is FilterValue => f !== undefined);
			setAppliedFilters(
				newDialogAppliedFilters.length > 0
					? newDialogAppliedFilters
					: [createDefaultFilterRow(filters)],
			);
			setErrors({});
		}
	}, [open, query.filters, filters]);

	// Add a new empty filter row
	const addFilterRow = useCallback(() => {
		setAppliedFilters((prev) => [...prev, createDefaultFilterRow(filters)]);
	}, [filters]);

	// Remove a filter row
	const removeFilter = useCallback((index: number) => {
		setAppliedFilters((prev) => prev.filter((_, i) => i !== index));
		setErrors((prev) => {
			const newErrors = { ...prev };
			delete newErrors[index];
			return newErrors;
		});
	}, []);

	// Update a filter row
	const updateFilterRow = useCallback(
		(index: number, field: Partial<FilterValue>) => {
			setAppliedFilters((prev) =>
				prev.map((f, i) => (i === index ? { ...f, ...field } : f)),
			);
		},
		[],
	);

	// Validation logic for a filter row
	const validateRow = useCallback(
		(row: FilterValue): string => {
			if (!row.id) return "Field is required.";
			const filterDef = filters.find((f) => f.id === row.id);
			if (!filterDef) return "Invalid field.";
			if (!row.op) return "Operator is required.";
			if (
				filterDef.type === "number" &&
				row.value &&
				Number.isNaN(Number(row.value))
			)
				return "Value must be a number.";
			if (
				filterDef.type === "date" &&
				row.value &&
				!/^\d{4}-\d{2}-\d{2}$/.test(row.value)
			)
				return "Value must be a date (YYYY-MM-DD).";
			if (row.value === "" || row.value === undefined)
				return "Value is required.";
			return "";
		},
		[filters],
	);

	const handleClearAll = async () => {
		setAppliedFilters([]);
		await query.setFiltersAsync([]);
		setErrors({});
		setOpen(false);
	};

	// Apply filters with validation
	const handleApplyFilters = async () => {
		console.log({
			appliedFilters,
		});
		const newErrors: Record<number, string> = {};
		appliedFilters.forEach((row, idx) => {
			const err = validateRow(row);
			if (err) newErrors[idx] = err;
		});
		setErrors(newErrors);
		//if (Object.keys(newErrors).length > 0) return;

		const queryFilters = appliedFilters
			.map((f) => {
				const filterDef = filters.find((filter) => filter.id === f.id);
				if (!filterDef) return null;

				const enforcedOp = enforceOperator(filterDef, f.op);

				if (filterDef.createFilter) {
					const created = filterDef.createFilter(f.value);
					return { ...created, op: enforcedOp };
				}

				const targetField = (filterDef.field ??
					filterDef.id) as FwQueryFilter<T>["field"];
				return {
					field: targetField,
					op: enforcedOp,
					value: String(f.value ?? ""),
				} as FwQueryFilter<T>;
			})
			.filter((filter) => filter !== null) as FwQueryFilter<T>[];

		await query.setFiltersAsync(queryFilters);
		setOpen(false);
	};

	// Get label for a filter with useMemo
	// const filterLabels = useMemo(() => {
	//   const labels: Record<string, string> = {};
	//   filters.forEach(filter => {
	//     labels[filter.id] = filter.label;
	//   });
	//   return labels;
	// }, [filters]);

	// Render select for field
	const renderFieldSelect = (row: FilterValue, idx: number) => {
		const selectedFilter = filters.find((f) => f.id === row.id);

		return (
			<Select
				value={row.id}
				onValueChange={(val) => {
					// When field changes, reset op and value
					const filter = filters.find((f) => f.id === val);
					if (filter) {
						updateFilterRow(idx, {
							id: val ?? undefined,
							op: getDefaultOperator(filter),
							value: "",
						});
					} else {
						updateFilterRow(idx, {
							id: val ?? undefined,
							op: DEFAULT_OPERATOR_OPTION.value as FilterOp,
							value: "",
						});
					}
				}}
			>
				<SelectTrigger>
					<SelectValue placeholder="Field">
						{selectedFilter?.label}
					</SelectValue>
				</SelectTrigger>
				<SelectContent>
					{filters.map((filter) => (
						<SelectItem key={filter.id} value={filter.id}>
							{filter.label}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
		);
	};

	// Render select for op
	const renderOpSelect = (row: FilterValue, idx: number) => {
		const filter = filters.find((f) => f.id === row.id);
		const { options: ops, isFixed } = filter
			? resolveOperatorOptions(filter)
			: { options: [DEFAULT_OPERATOR_OPTION], isFixed: false };
		return (
			<Select
				value={row.op}
				onValueChange={(val) => updateFilterRow(idx, { op: val as FilterOp })}
				disabled={isFixed}
			>
				<SelectTrigger>
					<SelectValue placeholder="Operator">
						{ops.find((op) => op.value === row.op)?.label}
					</SelectValue>
				</SelectTrigger>
				<SelectContent>
					{ops.map((op) => (
						<SelectItem key={op.value} value={op.value}>
							{op.label}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
		);
	};

	// Render input for value
	const renderValueInput = (row: FilterValue, idx: number) => {
		const filter = filters.find((f) => f.id === row.id);
		if (!filter)
			return <Input disabled placeholder="Value" value={row.value} />;
		switch (filter.type) {
			case "text":
				return (
					<Input
						value={row.value}
						onChange={(e) => updateFilterRow(idx, { value: e.target.value })}
						placeholder="Value"
					/>
				);
			case "select": {
				const selectedOption = filter.options?.find(
					(option) => option.value === row.value,
				);
				return (
					<Select
						value={row.value}
						onValueChange={(val) =>
							updateFilterRow(idx, { value: val ?? undefined })
						}
					>
						<SelectTrigger>
							<SelectValue placeholder="Value">
								{selectedOption?.label}
							</SelectValue>
						</SelectTrigger>
						<SelectContent>
							{filter.options?.map((option) => (
								<SelectItem key={option.value} value={option.value}>
									{option.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				);
			}
			case "date":
				return (
					<Input
						type="date"
						value={row.value}
						onChange={(e) => updateFilterRow(idx, { value: e.target.value })}
						placeholder="YYYY-MM-DD"
					/>
				);
			case "number":
				return (
					<Input
						type="number"
						value={row.value}
						onChange={(e) => updateFilterRow(idx, { value: e.target.value })}
						placeholder="Value"
					/>
				);
			case "boolean": {
				const selectedBooleanLabel =
					row.value === "true" ? "Yes" : row.value === "false" ? "No" : undefined;
				return (
					<Select
						value={row.value}
						onValueChange={(val) =>
							updateFilterRow(idx, { value: val ?? undefined })
						}
					>
						<SelectTrigger>
							<SelectValue placeholder="Value">
								{selectedBooleanLabel}
							</SelectValue>
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="true">Yes</SelectItem>
							<SelectItem value="false">No</SelectItem>
						</SelectContent>
					</Select>
				);
			}
			default:
				return (
					<Input
						value={row.value}
						onChange={(e) => updateFilterRow(idx, { value: e.target.value })}
						placeholder="Value"
					/>
				);
		}
	};

	return (
		<>
			<Button
				variant="outline"
				size="sm"
				onClick={() => setOpen(true)}
				className="mx-1"
			>
				<FilterIcon className="h-4 w-4 mr-2" />
				Filters
				{query.filters &&
					query.filters.filter((f) => f.field !== "search").length > 0 && (
						<span className="ml-2 bg-primary text-primary-foreground rounded-full px-2 py-0.5 text-xs">
							{query.filters.filter((f) => f.field !== "search").length}
						</span>
					)}
			</Button>
			<Button
				variant="outline"
				size="sm"
				onClick={handleClearAll}
				className="mx-1"
			>
				<XIcon className="h-4 w-4 mr-2" />
				Clear All
			</Button>
			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent className="sm:max-w-2xl lg:max-w-4xl">
					<DialogHeader>
						<DialogTitle>Filters</DialogTitle>
						<DialogDescription>
							Add, edit, or remove filters. All fields are required.
						</DialogDescription>
					</DialogHeader>
					<div className="py-4 space-y-2">
						{appliedFilters.map((row, idx) => (
							<div
								key={`${row.id}-${row.op}-${row.value}`}
								className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-[minmax(0,2fr)_minmax(0,1.2fr)_minmax(0,1.2fr)_auto] gap-2 border-b-2 py-2"
							>
								<div className="min-w-0">{renderFieldSelect(row, idx)}</div>
								<div className="min-w-0">{renderOpSelect(row, idx)}</div>
								<div className="min-w-0">{renderValueInput(row, idx)}</div>
								<div className="flex items-center justify-start lg:justify-center">
									<Button
										className="w-4"
										variant="ghost"
										size="icon"
										onClick={() => removeFilter(idx)}
									>
										<XIcon className="h-4 w-4 text-muted-foreground" />
									</Button>
								</div>
							</div>
						))}
						<div>
							<Button variant="outline" size="icon" onClick={addFilterRow}>
								<PlusIcon className="h-4 w-4" />
							</Button>
						</div>
						{Object.entries(errors).map(([idx, msg]) => (
							<div key={idx} className="text-xs text-red-600 mt-1 ml-2">
								{msg}
							</div>
						))}
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={handleClearAll}>
							Clear All
						</Button>
						<Button onClick={handleApplyFilters}>Apply Filters</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}

DataTableAdvancedFilters.displayName = "DataTableAdvancedFilters";

export { DataTableAdvancedFilters };
